import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, useScroll, useSpring, useTransform, useMotionValue, useReducedMotion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";
import { Spark, Squiggle, PatchBadge } from "@/components/Doodles";
import { useContent } from "@/context/ContentContext";

const POND_PADS = [
    { label: "ARTISTS", size: 150, color: "text-[#2E7D5B]", start: [0.03, 0.06], end: [0.38, 0.3], delay: 0 },
    { label: "MAKERS", size: 126, color: "text-[#27684C]", start: [0.26, 0.82], end: [0.56, 0.48], delay: 0.8 },
    { label: "TEACHERS", size: 126, color: "text-[#2E7D5B]", start: [0.64, 0.02], end: [0.44, 0.66], delay: 1.6 },
    { label: "MUSICIANS", size: 160, color: "text-[#27684C]", start: [0.9, 0.74], end: [0.6, 0.2], delay: 2.4 },
    { label: "", size: 84, color: "text-mustard", start: [0.47, 0.95], end: [0.5, 0.44], delay: 1.2 },
];

const PondPad = ({ pad, progress, size }) => {
    const reduced = useReducedMotion();
    const scaleFactor = size.w < 640 ? 0.62 : 1;
    const s = pad.size * scaleFactor;
    const x = useTransform(progress, (p) => (pad.start[0] + (pad.end[0] - pad.start[0]) * p) * size.w - s / 2);
    const y = useTransform(progress, (p) => (pad.start[1] + (pad.end[1] - pad.start[1]) * p) * size.h - s / 2);
    return (
        <motion.div className="absolute" style={{ x, y, width: s, height: s }}>
            <motion.div
                className="relative h-full w-full"
                animate={reduced ? undefined : { y: [0, -8, 0], rotate: [-2, 2, -2] }}
                transition={{ duration: 5, delay: pad.delay, repeat: Infinity, ease: "easeInOut" }}
            >
                <svg viewBox="0 0 100 100" className={`h-full w-full drop-shadow-[5px_5px_0_rgba(13,38,31,0.45)] ${pad.color}`}>
                    <path d="M50 50 L91.7 30.6 A46 46 0 1 0 91.7 69.4 Z" fill="currentColor" />
                </svg>
                {pad.label ? (
                    <span className="absolute inset-0 flex items-center justify-center pr-4 font-display text-xs tracking-[0.15em] text-cream -rotate-6 sm:text-sm">
                        {pad.label}
                    </span>
                ) : null}
            </motion.div>
        </motion.div>
    );
};

const Pond = () => {
    const ref = useRef(null);
    const reduced = useReducedMotion();
    const [size, setSize] = useState({ w: 900, h: 320 });

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const measure = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start 95%", "center 55%"] });
    const smooth = useSpring(scrollYProgress, { stiffness: 55, damping: 17 });
    const full = useMotionValue(1);
    const progress = reduced ? full : smooth;
    const sparkOpacity = useTransform(progress, [0.75, 1], [0, 1]);
    const sparkScale = useTransform(progress, [0.75, 1], [0.2, 1]);

    return (
        <div ref={ref} data-testid="lily-pond" className="relative h-80 sm:h-[26rem]" aria-label="Lily pads float together and meet in the middle">
            {POND_PADS.map((pad) => (
                <PondPad key={pad.label || "solo"} pad={pad} progress={progress} size={size} />
            ))}
            <motion.div
                className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2"
                style={{ opacity: sparkOpacity, scale: sparkScale }}
                aria-hidden="true"
            >
                <Spark className="h-14 w-14 text-mustard" />
            </motion.div>
        </div>
    );
};

export const LilyPadSection = () => {
    const { t } = useContent();
    const invite = t("home.lily_invite", []);
    return (
        <section data-testid="home-lilypad" className="border-b-2 border-ink bg-forest px-5 py-16 sm:px-10 sm:py-24">
            <div className="mx-auto max-w-6xl">
                <FadeUp>
                    <p className="font-hand text-2xl text-mustard -rotate-2 sm:text-3xl">{t("home.lily_kicker", "THE LILY PAD IDEA")}</p>
                    <h2 className="mt-2 max-w-3xl font-display text-4xl leading-[0.95] tracking-wide text-cream sm:text-5xl lg:text-6xl">
                        {t("home.lily_heading", "We can all float easier when we band together.")}
                    </h2>
                </FadeUp>

                <div className="mt-10 grid items-start gap-10 lg:grid-cols-[1.1fr_1fr]">
                    <FadeUp delay={0.1}>
                        <div className="border-2 border-ink bg-paper p-7 hard-shadow-lg -rotate-[0.5deg] sm:p-9">
                            <p className="leading-relaxed text-ink/80">{t("home.lily_body1", "")}</p>
                            <p className="mt-4 font-semibold leading-relaxed text-ink">{t("home.lily_body2", "")}</p>
                            <p className="mt-4 leading-relaxed text-ink/80">
                                <span className="font-display text-xl tracking-wide text-orange">{t("home.lily_body3_lead", "The Clubhouse is where the lily pads meet.")}</span>{" "}
                                {t("home.lily_body3", "It's a place to share space, audiences, resources, ideas, skills, and momentum.")}
                            </p>
                            <Squiggle className="mt-6 h-5 w-40 text-mustard" />
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.2}>
                        <Pond />
                    </FadeUp>
                </div>

                <FadeUp delay={0.1} className="mt-12">
                    <p className="font-display text-xl tracking-[0.15em] text-mustard">ROLL CALL — WE WANT LOCAL:</p>
                    <ul className="mt-4 flex flex-wrap gap-3">
                        {invite.map((who, i) => (
                            <li
                                key={who}
                                data-testid={`lily-invite-${who.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className={`border-2 border-ink bg-paper px-4 py-1.5 font-display text-sm tracking-[0.12em] text-ink hard-shadow transition-transform duration-200 hover:-translate-y-1 ${
                                    i % 2 ? "rotate-1" : "-rotate-1"
                                }`}
                            >
                                {who}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-6 max-w-2xl text-cream/85">{t("home.lily_note", "")}</p>
                </FadeUp>

                <FadeUp delay={0.15} className="mt-12 flex flex-col items-start gap-6 border-2 border-dashed border-cream/50 p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9">
                    <p className="max-w-xl font-display text-2xl leading-tight tracking-wide text-cream sm:text-3xl">
                        {t("home.lily_closing", "Got something cool you're trying to make happen?")}
                    </p>
                    <Link to="/contact" data-testid="lily-cta-button" className="btn-stamp !bg-mustard !text-ink !text-xl">
                        {t("home.lily_cta", "Tell Us Everything")}
                    </Link>
                </FadeUp>
            </div>
        </section>
    );
};

export const KidsTableSection = () => {
    const { t } = useContent();
    const paragraphs = t("home.kids_body", []);
    return (
        <section data-testid="home-kids-table" className="px-5 py-16 sm:px-10 sm:py-24">
            <div className="mx-auto max-w-6xl">
                <FadeUp>
                    <p className="font-hand text-2xl text-orange -rotate-2 sm:text-3xl">{t("home.kids_kicker", "WE DON'T BELIEVE IN THE KIDS TABLE")}</p>
                    <h2 className="mt-2 max-w-4xl font-display text-4xl leading-[0.98] tracking-wide text-ink sm:text-5xl">
                        {t("home.kids_heading", "")}
                    </h2>
                </FadeUp>
                <div className="mt-10 grid items-center gap-10 lg:grid-cols-[1.35fr_1fr]">
                    <FadeUp delay={0.12}>
                        <div className="relative border-2 border-ink bg-paper p-7 hard-shadow-lg rotate-[0.5deg] sm:p-10">
                        <span className="tape-strip" aria-hidden="true" />
                        {paragraphs.map((p, i) => (
                            <p key={i} className={`leading-relaxed text-ink/80 ${i ? "mt-4" : ""}`}>
                                {p}
                            </p>
                        ))}
                        <div className="mt-8 flex items-center gap-4">
                            <Spark className="h-8 w-8 shrink-0 text-orange" />
                            <p className="font-hand text-2xl leading-tight text-brick -rotate-1 sm:text-3xl">
                                {t("home.kids_closing", "Thriving communities don't warehouse generations. They weave them together.")}
                            </p>
                        </div>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.2}>
                        <div className="relative rotate-2 border-2 border-ink bg-paper p-3 hard-shadow-lg">
                            <span className="tape-strip" aria-hidden="true" />
                            <img src="https://cdn.stocksnap.io/img-thumbs/960w/GO3ZJSS5Z1.jpg" alt="Kids and elders making things together at the clubhouse" loading="lazy" className="aspect-[4/3] w-full border-2 border-ink object-cover sepia-[0.25] saturate-[0.85]" />
                            <p className="mt-2 pb-1 text-center font-hand text-xl text-brick -rotate-1">all ages, one table</p>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};

export const HelpBuildSection = () => {
    const { t } = useContent();
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", skills: "", availability: "", message: "" });
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/volunteers", form);
            setDone(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    return (
        <section data-testid="home-help-build" className="border-y-2 border-ink bg-orange px-5 py-16 sm:px-10 sm:py-20">
            <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
                <FadeUp>
                    <p className="font-hand text-2xl text-cream -rotate-2 sm:text-3xl">{t("home.help_kicker", "HELP BUILD THE THING")}</p>
                    <h2 className="mt-1 max-w-2xl font-display text-4xl leading-[0.95] tracking-wide text-cream sm:text-5xl lg:text-6xl">
                        {t("home.help_heading", "Volunteer, partner, organize, contribute, or otherwise get your hands dirty.")}
                    </h2>
                </FadeUp>
                <FadeUp delay={0.15} className="flex flex-col items-start gap-5">
                    <PatchBadge className="hidden h-24 w-24 -rotate-6 text-brick lg:block" />
                    <button
                        onClick={() => setFormOpen((v) => !v)}
                        data-testid="help-volunteer-button"
                        aria-expanded={formOpen}
                        className="btn-stamp !bg-cream !text-ink !text-xl"
                    >
                        {t("home.help_cta", "Sign Up to Volunteer")}
                    </button>
                </FadeUp>
                {formOpen && !done && (
                    <FadeUp className="mt-10 w-full">
                        <form onSubmit={submit} data-testid="volunteer-form" className="border-2 border-ink bg-paper p-7 hard-shadow-lg -rotate-[0.5deg] sm:p-9">
                            <h3 className="font-display text-2xl tracking-wide text-ink">RAISE YOUR HAND</h3>
                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="text-sm font-semibold text-ink" htmlFor="vol-name">Name</label>
                                    <input id="vol-name" data-testid="volunteer-name-input" required value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-ink" htmlFor="vol-email">Email</label>
                                    <input id="vol-email" data-testid="volunteer-email-input" type="email" required value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-ink" htmlFor="vol-skills">Skills, superpowers, or strong opinions</label>
                                    <input id="vol-skills" data-testid="volunteer-skills-input" value={form.skills}
                                        onChange={(e) => setForm({ ...form, skills: e.target.value })}
                                        placeholder="carpentry, kava, chaos coordination…"
                                        className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink placeholder:text-ink/40" />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-ink" htmlFor="vol-availability">Availability</label>
                                    <select id="vol-availability" data-testid="volunteer-availability-select" value={form.availability}
                                        onChange={(e) => setForm({ ...form, availability: e.target.value })}
                                        className="mt-1 w-full border-2 border-ink bg-cream px-3 py-3 text-ink">
                                        <option value="">Pick one…</option>
                                        <option>Weekdays</option>
                                        <option>Weeknights</option>
                                        <option>Weekends</option>
                                        <option>Whenever, honestly</option>
                                    </select>
                                </div>
                            </div>
                            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="vol-message">Anything else we should know?</label>
                            <textarea id="vol-message" data-testid="volunteer-message-input" rows={3} value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
                            <button data-testid="volunteer-submit-button" type="submit" disabled={busy} className="btn-stamp mt-5 !bg-orange disabled:opacity-60">
                                {busy ? "Licking the stamp…" : "Count Me In"}
                            </button>
                        </form>
                    </FadeUp>
                )}
                {done && (
                    <FadeUp className="mt-10 w-full">
                        <div data-testid="volunteer-success" className="stamp-frame border-2 border-ink bg-cream p-8 text-center">
                            <p className="font-display text-3xl tracking-wide text-forest">HAND RAISED. NOTED. CELEBRATED.</p>
                            <p className="mt-2 text-ink/75">You're on the volunteer list — we'll holler the moment there's a wall to paint or a shelf to build.</p>
                        </div>
                    </FadeUp>
                )}
            </div>
        </section>
    );
};


export const InsideCollage = () => {
    const { t } = useContent();
    const features = t("whatis.features", []);
    const ROTS = ["-2deg", "1.5deg", "-1deg", "2deg", "-1.5deg", "1deg", "-2deg", "1.5deg", "-1deg"];
    return (
        <section data-testid="home-inside-collage" className="border-y-2 border-ink bg-mustard px-5 py-16 sm:px-10 sm:py-24">
            <div className="mx-auto max-w-6xl">
                <FadeUp className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="font-hand text-2xl text-brick -rotate-2 sm:text-3xl">{t("home.inside_kicker", "THE GRAND TOUR (ABRIDGED)")}</p>
                        <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl lg:text-6xl">
                            {t("home.inside_heading", "WHAT'S INSIDE? A LITTLE OF EVERYTHING GOOD.")}
                        </h2>
                        <p className="mt-3 max-w-xl text-ink/75">{t("home.inside_note", "Nine-ish rooms, nooks, and excuses to leave your house.")}</p>
                    </div>
                    <Link to="/what-is-pcch" data-testid="inside-collage-cta" className="btn-stamp !bg-brick">
                        {t("home.inside_cta", "Step Inside")}
                    </Link>
                </FadeUp>
                <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3">
                    {features.map((f, i) => (
                        <FadeUp key={f.name} delay={(i % 3) * 0.07}>
                            <Link
                                to="/what-is-pcch"
                                data-testid={`inside-collage-item-${i}`}
                                className="group block border-2 border-ink bg-paper p-2.5 hard-shadow transition-transform duration-200 hover:-translate-y-1.5 hover:rotate-0"
                                style={{ transform: `rotate(${ROTS[i % ROTS.length]})` }}
                            >
                                <img src={f.photo} alt={f.name} loading="lazy" className="aspect-square w-full border-2 border-ink object-cover sepia-[0.25] saturate-[0.85]" />
                                <p className="px-1 pb-1 pt-2 text-center font-display text-sm tracking-[0.1em] text-ink sm:text-base">{f.name}</p>
                            </Link>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
};

export const DonateSection = () => {
    const { t } = useContent();
    const [amount, setAmount] = useState(25);
    const [custom, setCustom] = useState("");
    const [busy, setBusy] = useState(false);

    const donate = async () => {
        const value = custom ? Number(custom) : amount;
        if (!value || value < 1) {
            toast.error("A buck minimum — the paint fund has standards.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/payments/donate", { amount: value, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            setBusy(false);
        }
    };

    return (
        <section id="donate" data-testid="donate-section" className="border-t-2 border-ink bg-brick px-5 py-16 text-cream sm:px-10 sm:py-24">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
                <FadeUp>
                    <p className="font-hand text-2xl text-mustard -rotate-2 sm:text-3xl">{t("donate.kicker", "CHIP IN")}</p>
                    <h2 className="mt-2 font-display text-4xl leading-[0.95] tracking-wide sm:text-5xl lg:text-6xl">
                        {t("donate.heading", "MAKE A DONATION")}
                    </h2>
                    <p className="mt-5 max-w-xl leading-relaxed text-cream/85">{t("donate.body", "")}</p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        {[10, 25, 50, 100].map((v) => (
                            <button
                                key={v}
                                data-testid={`donate-preset-${v}`}
                                onClick={() => { setAmount(v); setCustom(""); }}
                                aria-pressed={!custom && amount === v}
                                className={`border-2 border-ink px-5 py-2.5 font-display text-xl tracking-wider hard-shadow transition-transform duration-150 hover:-translate-y-0.5 ${
                                    !custom && amount === v ? "bg-mustard text-ink" : "bg-cream/10 text-cream"
                                }`}
                            >
                                ${v}
                            </button>
                        ))}
                        <input
                            data-testid="donate-custom-input"
                            type="number"
                            min="1"
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            placeholder="$ custom"
                            aria-label="Custom donation amount"
                            className="w-32 border-2 border-ink bg-cream px-4 py-2.5 font-display text-xl text-ink placeholder:text-ink/40"
                        />
                    </div>
                    <button onClick={donate} disabled={busy} data-testid="donate-submit-button" className="btn-stamp mt-6 !bg-orange !text-xl disabled:opacity-60">
                        {busy ? "Passing the hat…" : t("donate.cta", "Donate")}
                    </button>
                </FadeUp>
                <FadeUp delay={0.15} className="hidden lg:block">
                    <div className="relative rotate-2 border-2 border-ink bg-paper p-3 hard-shadow-lg">
                        <span className="tape-strip" aria-hidden="true" />
                        <img
                            src="https://live.staticflickr.com/3885/14520767004_9a24e954cc_b.jpg"
                            alt="Neighbors pitching in together"
                            loading="lazy"
                            className="aspect-[4/3] w-full border-2 border-ink object-cover sepia-[0.25] saturate-[0.85]"
                        />
                        <p className="mt-2 pb-1 text-center font-hand text-xl text-brick -rotate-1">every dollar becomes a chair, a class, a coat of paint</p>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
};


export const SoftOpeningSection = () => {
    const { t } = useContent();
    const features = t("home.softopen_features", []);
    return (
        <section data-testid="soft-opening" className="border-b-2 border-ink bg-brick px-5 py-16 text-cream sm:px-10 sm:py-24">
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.15fr_1fr]">
                <div className="text-center lg:text-left">
                    <FadeUp>
                        <p className="inline-block -rotate-2 font-hand text-2xl text-mustard sm:text-3xl">{t("home.softopen_kicker", "mark the calendar in pen")}</p>
                        <h2 className="mt-2 font-display text-5xl leading-[0.95] tracking-wide sm:text-6xl lg:text-7xl">
                            {t("home.softopen_title", "FRIDAY THE 13TH SOFT OPENING")}
                        </h2>
                        <p className="mt-5 inline-block -rotate-1 border-2 border-ink bg-mustard px-4 py-2 font-display text-lg tracking-[0.12em] text-ink hard-shadow sm:text-xl">
                            {t("home.softopen_date", "FRIDAY, NOVEMBER 13, 2026")}
                        </p>
                        <p className="mt-4 font-display text-lg tracking-wider text-cream/90 sm:text-xl">{t("home.softopen_location", "7264 GLENVIEW DR, RICHLAND HILLS, TX 76180")}</p>
                        <p className="mt-3 max-w-2xl text-cream/85 lg:max-w-xl">
                            {t("home.softopen_note", "Come-and-go, stay as long as you like. First official day of the rest of the Clubhouse.")}
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.12} className="mt-9 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                        {features.map((f, i) => (
                            <span
                                key={f}
                                className={`inline-block border-2 border-ink bg-cream px-4 py-1.5 font-display text-base tracking-wider text-ink hard-shadow sm:text-lg ${i % 2 ? "rotate-1" : "-rotate-1"}`}
                            >
                                {f}
                            </span>
                        ))}
                    </FadeUp>
                    <FadeUp delay={0.2} className="mt-10">
                        <Link to="/whats-happening" data-testid="soft-opening-cta" className="btn-stamp !bg-mustard !text-ink !text-xl">
                            {t("home.softopen_cta", "See the Event Details")} →
                        </Link>
                    </FadeUp>
                </div>
                <FadeUp delay={0.15} className="mx-auto w-full max-w-sm">
                    <div className="relative rotate-2 border-2 border-ink bg-cream p-3 pb-5 hard-shadow-lg transition-transform duration-300 hover:rotate-0">
                        <span className="tape-strip" aria-hidden="true" />
                        <img
                            src={t("home.softopen_flyer", "/images/friday-13th-flyer.webp")}
                            alt="Friday the 13th Soft Opening flyer — Panther City Clubhouse, November 13 2026"
                            data-testid="soft-opening-flyer"
                            className="w-full border-2 border-ink object-cover"
                        />
                        <p className="mt-3 -rotate-1 text-center font-hand text-xl text-ink/70">the actual flyer. lucky you.</p>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
};

export const ResidentsSection = () => {
    const { t } = useContent();
    const residents = t("home.residents", []);
    return (
        <section data-testid="home-residents" className="border-b-2 border-ink bg-paper px-5 py-16 sm:px-10 sm:py-24">
            <div className="mx-auto max-w-6xl">
                <FadeUp>
                    <p className="font-hand text-2xl text-brick -rotate-2 sm:text-3xl">the fine folks holding keys</p>
                    <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl lg:text-6xl">MEET THE RESIDENTS</h2>
                    <p className="mt-3 max-w-2xl text-ink/75">{t("home.residents_intro", "The Clubhouse is home to a few small operations who make the place hum. Public spaces stay public; their rooms are theirs.")}</p>
                </FadeUp>
                <div className="mt-10 grid gap-6 sm:grid-cols-2">
                    {residents.map((r, i) => (
                        <FadeUp key={r.name} delay={i * 0.07}>
                            <div
                                data-testid={`resident-card-${i}`}
                                className={`relative h-full border-2 p-7 hard-shadow-lg transition-transform duration-200 hover:-translate-y-1 ${
                                    r.wanted
                                        ? "border-dashed border-cream/70 bg-brick text-cream " + (i % 2 ? "rotate-[0.5deg]" : "-rotate-[0.5deg]")
                                        : "border-ink bg-cream " + (i % 2 ? "rotate-1" : "-rotate-1")
                                }`}
                            >
                                <span className="tape-strip" aria-hidden="true" />
                                {r.wanted ? (
                                    <span className="inline-block -rotate-2 border-2 border-ink bg-mustard px-3 py-0.5 font-display text-xs tracking-[0.2em] text-ink hard-shadow">
                                        SPACE AVAILABLE
                                    </span>
                                ) : null}
                                <div className={r.photo ? "flex items-start gap-4" : ""}>
                                    {r.photo ? (
                                        <img
                                            src={r.photo}
                                            alt={r.name}
                                            loading="lazy"
                                            className="mt-3 h-24 w-24 shrink-0 -rotate-2 border-2 border-ink object-cover object-[50%_15%] hard-shadow"
                                        />
                                    ) : null}
                                    <div>
                                        <h3 className={`mt-3 font-display text-2xl tracking-wide sm:text-3xl ${r.wanted ? "text-cream" : "text-ink"}`}>{r.name}</h3>
                                        <p className={`mt-1 font-hand text-xl ${r.wanted ? "text-mustard" : "text-brick"}`}>{r.role}</p>
                                    </div>
                                </div>
                                <p className={`mt-3 leading-relaxed ${r.wanted ? "text-cream/85" : "text-ink/75"}`}>{r.blurb}</p>
                                {r.link ? (
                                    <Link to={r.link} data-testid={`resident-cta-${i}`} className="btn-stamp mt-5 inline-block !bg-mustard !text-ink">
                                        {r.link_label || "Say Hello"} →
                                    </Link>
                                ) : null}
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
};

export const ValuesSection = () => {
    const { t } = useContent();
    const values = t("home.values", []);
    return (
        <section data-testid="home-values" className="border-y-2 border-ink bg-mustard px-5 py-14 sm:px-10 sm:py-20">
            <div className="mx-auto max-w-6xl text-center">
                <FadeUp>
                    <p className="inline-block -rotate-2 font-hand text-2xl text-brick sm:text-3xl">how we keep it weird and kind</p>
                    <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl">THE HOUSE VALUES</h2>
                </FadeUp>
                <FadeUp delay={0.1} className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    {values.map((v, i) => (
                        <span
                            key={v}
                            data-testid={`value-chip-${i}`}
                            className={`inline-block border-2 border-ink bg-cream px-4 py-2 font-display text-base tracking-wider text-ink hard-shadow sm:text-lg ${i % 2 ? "rotate-1" : "-rotate-1"}`}
                        >
                            {v}
                        </span>
                    ))}
                </FadeUp>
                <FadeUp delay={0.18}>
                    <p className="mx-auto mt-6 max-w-2xl text-ink/80">
                        {t("home.values_note", "Kids, adults, and elders share the same tables here — same glue sticks, same games, same door. Intergenerational on purpose.")}
                    </p>
                </FadeUp>
            </div>
        </section>
    );
};
